import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { TranslatePipe, TranslationService } from '@kirigami/shared/i18n';
import { FileService } from '../../../services/file.service';
import { SupportService } from '../../../services/support.service';
import { ToastService } from '../../../services/toast.service';

/** Mirrors the design's enablement rule: a plausible email and a message. */
export function contactFormValid(email: string, message: string): boolean {
  return /.+@.+\..+/.test(email.trim()) && message.trim().length > 0;
}

/** Matches the server: png/jpg/jpeg up to 5 MB (spec 2026-09-04 §9). */
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ACCEPTED_ATTACHMENT_TYPES = ['image/png', 'image/jpeg'];

/** Why an attachment cannot be sent, or null when it can. */
export function attachmentRejection(file: File): 'type' | 'size' | null {
  if (!ACCEPTED_ATTACHMENT_TYPES.includes(file.type)) return 'type';
  if (file.size > MAX_ATTACHMENT_BYTES) return 'size';
  return null;
}

/**
 * Contact modal, per the landing refinement design: scrim + card with email,
 * message and an attachment slot, Cancel/Send footer. Send is enabled once
 * the form validates and submits through the shared SupportService
 * (`POST /v1/feedbacks/`), tagged with source `landing-contact`. A picked
 * image uploads to S3 after the feedback row is created; if that upload
 * fails, the toast says so instead of pretending the image arrived.
 */
@Component({
  selector: 'app-contact-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    @if (open) {
      <div
        class="cm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cm-title"
        (click)="onScrim($event)"
      >
        <div class="cm__card">
          <h3 class="cm__title" id="cm-title">{{ 'landing.contactModal.title' | t }}</h3>
          <p class="cm__intro">{{ 'landing.contactModal.intro' | t }}</p>

          <div class="cm__fields">
            <div>
              <label class="cm__label" for="cm-email">{{
                'landing.contactModal.emailLabel' | t
              }}</label>
              <input
                #emailField
                id="cm-email"
                class="cm__input"
                type="email"
                autocomplete="email"
                [placeholder]="'landing.contactModal.emailPlaceholder' | t"
                [value]="email()"
                (input)="email.set(emailField.value)"
              />
            </div>
            <div>
              <label class="cm__label" for="cm-message">{{
                'landing.contactModal.messageLabel' | t
              }}</label>
              <textarea
                #messageField
                id="cm-message"
                class="cm__input cm__textarea"
                rows="4"
                [placeholder]="'landing.contactModal.messagePlaceholder' | t"
                [value]="message()"
                (input)="message.set(messageField.value)"
              ></textarea>
            </div>
            <div>
              <span class="cm__label">{{ 'landing.contactModal.attachmentLabel' | t }}</span>
              <div class="cm__attach">
                @if (fileName()) {
                  <span class="cm__attach-name">{{ fileName() }}</span>
                  <button type="button" class="cm__attach-clear" (click)="clearFile()">
                    {{ 'landing.contactModal.remove' | t }}
                  </button>
                } @else {
                  <button type="button" class="cm__attach-choose" (click)="fileInput.click()">
                    {{ 'landing.contactModal.choose' | t }}
                  </button>
                  <span class="cm__attach-hint">{{
                    'landing.contactModal.attachmentHint' | t
                  }}</span>
                }
                <input
                  #fileInput
                  type="file"
                  class="cm__file"
                  accept="image/png,image/jpeg"
                  (change)="onFilePicked(fileInput.files)"
                />
              </div>
            </div>
          </div>

          @if (error()) {
            <p class="cm__error" role="alert">{{ error() }}</p>
          }

          <div class="cm__footer">
            <button type="button" class="cm__btn cm__btn--cancel" (click)="close()">
              {{ 'landing.contactModal.cancel' | t }}
            </button>
            <button
              type="button"
              class="cm__btn cm__btn--send"
              [disabled]="!valid() || sending()"
              (click)="send()"
            >
              {{ (sending() ? 'landing.contactModal.sending' : 'landing.contactModal.send') | t }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [
    `
      .cm {
        position: fixed;
        inset: 0;
        z-index: 60;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
      }
      .cm__card {
        width: 100%;
        max-width: 35rem;
        max-height: 86vh;
        overflow: auto;
        padding: 2.5rem;
        border-radius: 1rem;
        background: var(--neu-0);
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
        animation: cm-scale-in 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      @media (prefers-reduced-motion: reduce) {
        .cm__card {
          animation: none;
        }
      }
      .cm__title {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1.3;
        color: var(--kirigami-600);
      }
      .cm__intro {
        margin: 0.875rem 0 1.875rem;
        font-size: 1rem;
        line-height: 1.6;
        color: var(--neu-800);
        text-wrap: pretty;
      }
      .cm__fields {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }
      .cm__label {
        display: block;
        margin: 0 0 0.5rem;
        font-size: 1rem;
        font-weight: 700;
        color: var(--neu-900);
      }
      /* neu-400 borders (not the design's neu-200) so the field boundary
         meets the 3:1 AA non-text contrast minimum. */
      .cm__input {
        width: 100%;
        padding: 0.8125rem 0.9375rem;
        border: 1px solid var(--neu-400);
        border-radius: 0.5rem;
        background: var(--neu-0);
        font-family: inherit;
        font-size: 1rem;
        color: var(--neu-900);
        transition: border-color 0.18s ease;
      }
      .cm__input:focus-visible {
        outline: none;
        border-color: var(--kirigami-500);
        box-shadow:
          inset 0 0 0 2px rgba(255, 255, 255, 1),
          0 0 0 2px rgba(77, 140, 185, 1);
      }
      .cm__textarea {
        min-height: 8.25rem;
        resize: vertical;
        line-height: 1.6;
      }
      .cm__attach {
        padding: 1.625rem;
        border: 1px dashed var(--neu-200);
        border-radius: 0.5rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.875rem;
      }
      .cm__attach-choose {
        padding: 0.5625rem 1.25rem;
        border: 1px solid var(--neu-200);
        border-radius: 0.5rem;
        background: var(--neu-50);
        font-family: inherit;
        font-size: 0.9375rem;
        color: var(--neu-900);
        cursor: pointer;
        transition: background-color 0.18s ease;
      }
      .cm__attach-choose:hover {
        background: var(--neu-100);
      }
      .cm__attach-hint {
        font-size: 0.875rem;
        /* neu-500 so the hint meets the 4.5:1 AA text contrast minimum. */
        color: var(--neu-500);
      }
      .cm__attach-name {
        font-size: 0.9375rem;
        color: var(--neu-900);
        word-break: break-all;
      }
      .cm__attach-clear {
        border: 0;
        background: none;
        font-family: inherit;
        font-size: 0.875rem;
        color: var(--kirigami-600);
        cursor: pointer;
      }
      .cm__attach-clear:hover {
        color: var(--kirigami-700);
      }
      .cm__file {
        display: none;
      }
      .cm__error {
        margin: 1rem 0 0;
        font-size: 0.875rem;
        color: var(--pink-600);
      }
      .cm__footer {
        margin: 2.125rem 0 0;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
      }
      .cm__btn {
        height: 3.5rem;
        border-radius: 2rem 0.25rem 1.25rem 0.25rem;
        font-family: inherit;
        font-size: 1rem;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        cursor: pointer;
        transition:
          background-color 0.18s ease,
          color 0.18s ease;
      }
      /* kirigami-600 (not the design's 500) so both 16px labels meet the
         4.5:1 AA contrast minimum. */
      .cm__btn--cancel {
        border: 2px solid var(--kirigami-600);
        background: var(--neu-0);
        color: var(--kirigami-600);
      }
      .cm__btn--cancel:hover {
        background: var(--kirigami-600);
        color: var(--neu-0);
      }
      .cm__btn--send {
        border: 0;
        background: var(--kirigami-600);
        color: var(--neu-0);
      }
      .cm__btn--send:hover:not(:disabled) {
        background: var(--kirigami-700);
      }
      .cm__btn--send:disabled {
        background: var(--kirigami-300);
        cursor: default;
      }
      @keyframes cm-scale-in {
        from {
          opacity: 0;
          transform: scale(0.97) translateY(8px);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }
    `,
  ],
})
export class ContactModalComponent {
  private readonly supportService = inject(SupportService);
  private readonly toastService = inject(ToastService);
  private readonly fileService = inject(FileService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() set open(value: boolean) {
    this.isOpen = value;
    if (value) {
      this.error.set(null);
      // Remember the trigger so focus returns there on close, and move
      // focus to the email field once the card has rendered.
      this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setTimeout(() => {
        this.host.nativeElement.querySelector<HTMLInputElement>('#cm-email')?.focus();
      }, 60);
    }
  }
  get open(): boolean {
    return this.isOpen;
  }
  private isOpen = false;
  private opener: HTMLElement | null = null;

  @Output() readonly dismissed = new EventEmitter<void>();

  @ViewChild('emailField') emailField?: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  private readonly translations = inject(TranslationService);

  readonly email = signal('');
  readonly message = signal('');
  readonly file = signal<File | null>(null);
  readonly fileName = computed(() => this.file()?.name ?? '');
  readonly sending = signal(false);
  readonly error = signal<string | null>(null);
  readonly valid = computed(() => contactFormValid(this.email(), this.message()));

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen && !this.sending()) this.close();
  }

  onScrim(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.sending()) this.close();
  }

  onFilePicked(files: FileList | null): void {
    const picked = files?.[0] ?? null;
    if (!picked) {
      this.file.set(null);
      return;
    }

    const rejection = attachmentRejection(picked);
    if (rejection) {
      this.file.set(null);
      this.resetFileInput();
      this.error.set(
        this.translations.t(
          rejection === 'type'
            ? 'landing.contactModal.typeError'
            : 'landing.contactModal.sizeError',
        ),
      );
      return;
    }

    this.error.set(null);
    this.file.set(picked);
  }

  clearFile(): void {
    this.file.set(null);
    this.resetFileInput();
  }

  /**
   * The native input keeps its value after Remove or a rejected pick, so
   * choosing the same file again would otherwise fire no `change` event.
   */
  private resetFileInput(): void {
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  close(): void {
    this.dismissed.emit();
    this.opener?.focus();
    this.opener = null;
  }

  send(): void {
    if (!this.valid() || this.sending()) return;
    this.sending.set(true);
    this.error.set(null);

    const file = this.file();

    this.supportService
      .submitFeedback({
        sender: this.email().trim(),
        message: this.message().trim(),
        source: 'landing-contact',
        attachment: file?.name ?? null,
      })
      .subscribe({
        next: (created) => {
          if (file && created?.id) {
            this.uploadAttachment(created.id, file);
            return;
          }
          this.finish(!file);
        },
        error: (err: Error) => {
          this.sending.set(false);
          this.error.set(err.message);
        },
      });
  }

  /**
   * The message is already stored and mailed by now, so an upload failure is a
   * partial success: say so in the toast rather than losing the submission.
   */
  private uploadAttachment(feedbackId: string, file: File): void {
    this.supportService.getAttachmentUploadUrl(feedbackId, file.name).subscribe({
      next: (presigned) => {
        this.fileService.uploadFile(presigned, file).subscribe({
          next: () => this.finish(true),
          error: () => this.finish(false),
        });
      },
      error: () => this.finish(false),
    });
  }

  private finish(attachmentDelivered: boolean): void {
    this.sending.set(false);
    this.email.set('');
    this.message.set('');
    this.file.set(null);
    this.toastService.success(this.translations.t('landing.contactModal.successToast'), {
      body: this.translations.t(
        attachmentDelivered
          ? 'landing.contactModal.successBody'
          : 'landing.contactModal.successWithoutImage',
      ),
    });
    this.close();
  }
}
